import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type {
  ListConversationsResponse,
  AnalyticsResponse,
  ConversationListItem,
  ConversationStatus,
  Language,
} from '@shared/types';
import apiService from '../../services/api.service';
import { removeToken, getAdminInfo } from '../../services/auth.service';

/**
 * AdminDashboardPage Component
 * 
 * Main admin dashboard with conversation list, filters, and analytics.
 * 
 * Requirements:
 * - US3: Admin interface for viewing conversations
 * - FR-024: Conversation management
 */

export const AdminDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const adminInfo = getAdminInfo();
  
  const [conversations, setConversations] = useState<ConversationListItem[]>([]);
  const [analytics, setAnalytics] = useState<AnalyticsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<ConversationStatus | ''>('');
  const [languageFilter, setLanguageFilter] = useState<Language | ''>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  /**
   * Load conversations
   */
  const loadConversations = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const params: any = {
        page: currentPage,
        limit: 20,
      };

      if (statusFilter) params.status = statusFilter;
      if (languageFilter) params.language = languageFilter;

      const response = await apiService.get<ListConversationsResponse>(
        '/api/admin/conversations',
        { params }
      );

      setConversations(response.data.conversations);
      setTotalPages(response.data.pagination.totalPages);
    } catch (err: any) {
      console.error('Failed to load conversations:', err);
      setError(t('admin.dashboard.errors.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Load analytics
   */
  const loadAnalytics = async () => {
    try {
      const response = await apiService.get<AnalyticsResponse>('/api/admin/analytics');
      setAnalytics(response.data);
    } catch (err) {
      console.error('Failed to load analytics:', err);
    }
  };

  /**
   * Load data on mount and when filters change
   */
  useEffect(() => {
    loadConversations();
  }, [statusFilter, languageFilter, currentPage]);

  useEffect(() => {
    loadAnalytics();
  }, []);

  /**
   * Handle logout
   */
  const handleLogout = () => {
    removeToken();
    navigate('/admin/login');
  };

  /**
   * Get status badge color
   */
  const getStatusColor = (status: ConversationStatus) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'IN_PROGRESS':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'INCOMPLETE':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {t('admin.dashboard.title')}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {t('admin.dashboard.welcome', { email: adminInfo?.email })}
            </p>
          </div>
          <button
            onClick={handleLogout}
            className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            {t('admin.dashboard.logout')}
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Analytics Cards */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                {t('admin.dashboard.analytics.total')}
              </p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {analytics.totalConversations}
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                {t('admin.dashboard.analytics.completed')}
              </p>
              <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                {analytics.completedConversations}
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                {t('admin.dashboard.analytics.completionRate')}
              </p>
              <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                {analytics.completionRate}%
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                {t('admin.dashboard.analytics.avgCompletion')}
              </p>
              <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                {analytics.averageCompletionPoint.toFixed(1)}
              </p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {t('admin.dashboard.filters.title')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('admin.dashboard.filters.status')}
              </label>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as any);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">{t('admin.dashboard.filters.allStatuses')}</option>
                <option value="COMPLETED">{t('admin.dashboard.filters.completed')}</option>
                <option value="IN_PROGRESS">{t('admin.dashboard.filters.inProgress')}</option>
                <option value="INCOMPLETE">{t('admin.dashboard.filters.incomplete')}</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('admin.dashboard.filters.language')}
              </label>
              <select
                value={languageFilter}
                onChange={(e) => {
                  setLanguageFilter(e.target.value as any);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">{t('admin.dashboard.filters.allLanguages')}</option>
                <option value="HU">🇭🇺 Magyar</option>
                <option value="EN">🇬🇧 English</option>
              </select>
            </div>
          </div>
        </div>

        {/* Conversations Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('admin.dashboard.conversationsTitle')}
            </h2>
          </div>

          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}

          {isLoading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
              <p className="mt-4 text-gray-600 dark:text-gray-400">
                {t('admin.dashboard.loading')}
              </p>
            </div>
          ) : conversations.length === 0 ? (
            <div className="p-12 text-center text-gray-600 dark:text-gray-400">
              {t('admin.dashboard.noConversations')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('admin.dashboard.table.visitor')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('admin.dashboard.table.status')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('admin.dashboard.table.progress')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('admin.dashboard.table.language')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('admin.dashboard.table.created')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('admin.dashboard.table.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {conversations.map((conv) => (
                    <tr
                      key={conv.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {conv.visitor.name}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {conv.visitor.email}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                            conv.status
                          )}`}
                        >
                          {conv.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {conv.completionRate}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {conv.language === 'HU' ? '🇭🇺 Magyar' : '🇬🇧 English'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {new Date(conv.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => navigate(`/admin/conversations/${conv.id}`)}
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          {t('admin.dashboard.table.view')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!isLoading && conversations.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('admin.dashboard.pagination.page', {
                  current: currentPage,
                  total: totalPages,
                })}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('admin.dashboard.pagination.previous')}
                </button>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('admin.dashboard.pagination.next')}
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};
